import { parseQueryParamsObject } from "@robe/core-utils";
import { GetContentsOptions, GetContentsOptionsBase, GetContentsSource, QueryOptionsBase } from "../types";
import { Content } from "@robe/core-types";
import { XcoreCore } from "XcoreCore";
import { resolveFetchPublishedOnly, resolveLocaleParams } from "./queryParamsResolvers";

const getBaseContentsQuery = ({
  core,
  fields,
  locales,
  categories,
  includeValues,
  includeCategories,
  createdAt,
  publishedAt,
  publishedOnly,
  relations,
}: GetContentsOptions & { core: XcoreCore }) =>
  parseQueryParamsObject(
    {
      fields,
      categories,
      includeValues,
      includeCategories,
      createdAt,
      publishedAt,
      relations,
      ...resolveFetchPublishedOnly(publishedOnly),
      ...resolveLocaleParams(core, locales),
    },
    { omitQuestionMark: true }
  );

const euc = encodeURIComponent;

/**
 * Generates a query string for fetching content based on provided options.
 *
 * @param source.types Content type or array of content types
 * @param source.ids Content ID or array of content IDs
 * @param options An object containing options for sorting, categorization, and filtering the content.
 * @param options.sort A sorting option for the content.
 * @param options.categories Categories to filter the content by.
 *
 * @returns A string representing the query for fetching content.
 */
const getSpecialContentsQuery = <T extends Content>(
  source: GetContentsSource<T>,
  { sort, categories }: Pick<GetContentsOptionsBase, "sort" | "categories">
) =>
    [
      source &&
      "types" in source &&
      source?.types &&
      (Array.isArray(source.types) ? source.types.map((type) => `types=${euc(type)}`).join("&") : `types=${euc(source?.types)}`),
      source &&
      "ids" in source &&
      source?.ids &&
      (Array.isArray(source.ids) ? source.ids.map((id) => `ids=${euc(id)}`).join("&") : `ids=${euc(source.ids)}`),
      categories?.map((r, x) => r.map((c, y) => `categories[${euc(x)}][${euc(y)}]=${euc(c)}`).join("&")).join("&"),
      // order
      sort &&
      Object.entries(sort)
        .map(([sortKey, sortValue]) => `sort[0].${euc(sortKey)}=${euc(sortValue)}`)
        .join("&"),
    ]
      .filter(Boolean)
      .join("&");

const paginateQuery = (page?: number, perPage?: number) => parseQueryParamsObject({ page, perPage }, { omitQuestionMark: true });

export const getContentsBaseQueries = <T extends Content>(
  core: XcoreCore,
  source: GetContentsSource<T>,
  options: GetContentsOptionsBase & QueryOptionsBase
) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  const { categories, ...restOptions } = options;

  return [
    getSpecialContentsQuery(source, options),
    getBaseContentsQuery({ core, ...restOptions }),
    paginateQuery(options.page, options.perPage),
  ]
    .filter(Boolean)
    .join("&");
};
